'use client';

import { QR_CODE_RADIUS_DISPLAY } from '@components/CardAccessory/CardAccessoryConstants';
import useNode from '@hooks/use-node';
import useRemoteImageBlob from '@hooks/use-remote-image-blob';
import { drawRoundRect } from '@modules/draw-round-rect';
import { getBoundedDimensions } from '@modules/image-helpers';
import * as BaseQRCode from 'qrcode';
import { useEffect, useImperativeHandle, useState } from 'react';

import styles from './QRCode.module.scss';

interface QRCodeBaseProps {
  size: number;
  /**
   * Customize the display size when you want to maintain a high-resolution image for downloading the QR code,
   * but display a smaller version of it.
   */
  displaySize?: number;
  value: string;
  errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H';
  darkColor?: string;
  lightColor?: string;
  imageSrc?: string;
  showImage?: boolean;
  imageBackgroundColor?: string;
  imageBorderColor?: string;
  imageShadow?: boolean;
  imageBorderRadius?: number;
  margin?: number;
  showBorder?: boolean;
}

interface QRCodeDisplayProps extends QRCodeBaseProps {
  /** The default purpose, usually to show a QR code for a Blinq card. */
  purpose?: 'display';
}
interface QRCodeLoginProps extends QRCodeBaseProps {
  /** The QR Code is for session continuity login */
  purpose: 'login';
  /** use 150 for small spaces, and 320 for large */
  size: 150 | 320 | number;
  /**  low can cause problems due to long URL value */
  errorCorrectionLevel: 'M';
}

// discriminated union type
type QRCodeProps = QRCodeDisplayProps | QRCodeLoginProps;

export interface QRCodeHandle {
  downloadQRCode: (filename: string) => void;
  getDataURL: () => string | undefined;
  getCanvas: () => HTMLCanvasElement | undefined;
}

const IMAGE_PADDING = 6;
const MAX_IMAGE_BLOCKS = 5;
const TOTAL_IMAGE_BLOCKS = 33;

function setupCanvas(canvas: HTMLCanvasElement): CanvasRenderingContext2D | null {
  // Get the device pixel ratio, falling back to 1.
  const dpr = window.devicePixelRatio || 1;

  const ctx = canvas.getContext('2d');
  // Scale all drawing operations by the dpr, so you
  // don't have to worry about the difference.
  ctx?.scale(dpr, dpr);
  return ctx;
}

function resetCanvasTransform(ctx: CanvasRenderingContext2D): void {
  // Reset current transformation matrix to the identity matrix
  ctx.setTransform(1, 0, 0, 1, 0, 0);
}

const QRCode = ({
  ref: forwardedRef,
  size,
  displaySize = size,
  value,
  errorCorrectionLevel = 'M',
  darkColor = '#000000ff',
  lightColor = '#ffffffff',
  imageSrc,
  showImage = true,
  imageBackgroundColor = '#fff',
  imageBorderColor = '#fff',
  imageShadow = false,
  imageBorderRadius = 0,
  margin = 2,
  showBorder = false,
}: QRCodeProps & {
  ref?: React.Ref<QRCodeHandle>;
}) => {
  const [loadingQRCode, setLoadingQRCode] = useState(true);
  const [canvasNode, canvasRef] = useNode<HTMLCanvasElement>();

  // We need to download the image to a blob that we can use locally, otherwise the canvas becomes tainted and
  // we can't download it (browser security detail).
  const { imageUrl } = useRemoteImageBlob(imageSrc);

  const finalImageUrl = imageUrl || imageSrc; //in case the image was a local base64 string

  // The image has to be smaller than a certain percentage of the QR code otherwise the QR code
  // will become unusable. This depends on the error correction level and the size of the QR code.
  // The number I'm using here is essentially an educated guess.
  const maxImageSize = size * (MAX_IMAGE_BLOCKS / TOTAL_IMAGE_BLOCKS) - 2 * IMAGE_PADDING;
  const imageBackgroundSize = Math.ceil(size * (MAX_IMAGE_BLOCKS / TOTAL_IMAGE_BLOCKS));

  useEffect(() => {
    if (canvasNode) {
      BaseQRCode.toCanvas(
        canvasNode,
        value,
        {
          margin: margin,
          width: size * (window.devicePixelRatio || 1),
          errorCorrectionLevel,
          color: { dark: darkColor, light: lightColor },
        },
        (error) => {
          canvasNode.style.width = `${displaySize}px`;
          canvasNode.style.height = `${displaySize}px`;

          if (showImage && imageSrc) {
            const ctx = setupCanvas(canvasNode);
            if (!ctx) {
              return;
            }

            // This serves three purposes. It gives a background to transparent images. It adds some padding between
            // the image and the QR code modules (the dark bits). And it makes the QR code look nicer.
            drawRoundRect(
              ctx,
              size / 2 - (imageBackgroundSize - 2) / 2,
              size / 2 - (imageBackgroundSize - 2) / 2,
              imageBackgroundSize,
              imageBackgroundSize - 1.5, // I don't know why I need the extra cut off but I do
              imageBorderRadius,
              imageBackgroundColor,
              imageBorderColor,
              imageShadow,
            );

            resetCanvasTransform(ctx);
          }

          setLoadingQRCode(false);

          if (error) {
            // biome-ignore lint: biomelint/correctness/noConsoleLogs
            console.error(error);
          }
        },
      );
    }
  }, [
    canvasNode,
    value,
    size,
    displaySize,
    errorCorrectionLevel,
    darkColor,
    lightColor,
    showImage,
    imageBackgroundColor,
    imageBorderColor,
    imageShadow,
    imageBackgroundSize,
    imageSrc,
    imageBorderRadius,
    margin,
  ]);

  useEffect(() => {
    if (loadingQRCode || !canvasNode || !showImage || !finalImageUrl) {
      return;
    }

    const image = new Image();

    function handleImageLoad(this: GlobalEventHandlers) {
      if (!canvasNode) {
        return;
      }
      const ctx = setupCanvas(canvasNode);
      if (!ctx) {
        return;
      }

      const { width, height } = getBoundedDimensions(image, maxImageSize, maxImageSize);

      // Where to place the image
      const dx = size / 2 - width / 2;
      const dy = size / 2 - height / 2;
      ctx.drawImage(image, dx, dy, width, height);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
    }

    image.onload = handleImageLoad;
    // We need this line to ensure browser CORS allows us to download the canvas with this image in it (if not it's treated as a tainted canvas).
    image.crossOrigin = 'Anonymous';
    image.src = finalImageUrl;
  }, [canvasNode, loadingQRCode, showImage, size, maxImageSize, finalImageUrl]);

  useImperativeHandle(forwardedRef, () => ({
    downloadQRCode: (filename: string) => {
      if (!canvasNode) {
        return;
      }

      canvasNode.toBlob((blob) => {
        if (blob) {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          link.click();
        }
      });
    },
    getDataURL: () => {
      if (!canvasNode) {
        return;
      }

      return canvasNode.toDataURL();
    },
    getCanvas: () => canvasNode,
  }));

  return (
    <div className={styles['qr-code']}>
      {showBorder && (
        <svg className={styles['qr-code-border-svg']} width="100%" viewBox="0 0 100 100">
          <rect fill="white" rx={QR_CODE_RADIUS_DISPLAY} x={1} y={1} width={99} height={99} />
        </svg>
      )}
      <canvas className={styles['qr-code-canvas']} ref={canvasRef}></canvas>
    </div>
  );
};

// adds a fix for weird chrome behaviour BQW-16
const QRCodeWithStatefulReload = ({
  ref: forwardedRef,
  ...p
}: QRCodeProps & {
  ref?: React.Ref<QRCodeHandle>;
}) => {
  const key = `${p.showImage}${p.imageSrc}`; // reload when the src or toggle change
  return <QRCode key={key} ref={forwardedRef} {...p} />;
};

QRCode.displayName = 'QRCode';
QRCodeWithStatefulReload.displayName = 'QRCode';

export default QRCodeWithStatefulReload;
